import intl from 'react-intl-universal';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useModel } from 'umi';
import { message, Tag } from 'antd';
import { default as Button } from '@/components/CssBtn';
import { default as Modal } from '@/components/CssModal';

import { getOrderList } from '@/api/order';

import { DynamicTable } from '@/components/index';

import type { ProColumnType } from '@ant-design/pro-components';
import type { FormItemValueType } from '@/components/index';

interface Props {
    open: boolean;
    selectOrders: string[];
    maxlength?: number;
    onClose: (isOpen: boolean) => void;
    onConfirm: (orders: string[]) => void;
}

const SelectOrders: React.FC<Props> = ({
    open,
    selectOrders: originSelectOrders = [],
    maxlength = 100,
    onClose,
    onConfirm,
}) => {
    const { afterSaleEnumList } = useModel('global');

    const tableRef = useRef();
    const formRef = useRef();

    const [selectOrders, setSelectOrders] =
        useState<string[]>(originSelectOrders);
    const [disabled, setDisabled] = useState<boolean>(true);

    useEffect(() => {
        setSelectOrders(originSelectOrders);
    }, [originSelectOrders]);

    const columns: ProColumnType<any, FormItemValueType>[] = useMemo(
        () => [
            {
                title: intl.get('渠道订单号'),
                dataIndex: 'business_child_order_no',
                // hideInSearch: true,
                width: 200,
                disabled: true,
                fixed: true,
                fieldProps: {
                    placeholder: intl.get('请输入订单号'),
                },
                // formItemProps: {
                //     wrapperCol: { span: 18 },
                // },
            },
            {
                title: intl.get('顾客手机号'),
                dataIndex: 'mobile',
                hideInTable: true,
                hideInSearch: true,
                // fixed: true,
            },
            {
                title: intl.get('支付时间'),
                dataIndex: 'pay_at',
                hideInTable: true,
                valueType: 'dateRange',
                hideInSearch: true,
                // fixed: true,
            },
            {
                title: intl.get('自有渠道'),
                dataIndex: 'order_channel',
                hideInTable: true,
                valueType: 'select',
                fieldProps: {
                    options: afterSaleEnumList?.selfBuyChannel || [],
                },
                hideInSearch: true,
            },

            {
                title: intl.get('OMS交易号'),
                dataIndex: 'send_order_no',
                hideInSearch: true,
                width: 200,
                disabled: true,
            },
            {
                title: intl.get('订单类型'),
                dataIndex: 'order_type_str',
                hideInSearch: true,
                width: 100,
                disabled: true,
            },
            {
                title: intl.get('状态'),
                dataIndex: 'status_str',
                hideInSearch: true,
                width: 100,
                disabled: true,
            },
            {
                title: intl.get('订单金额'),
                dataIndex: 'payment_price',
                hideInSearch: true,
                width: 100,
                disabled: true,
            },
            {
                title: intl.get('购买渠道'),
                dataIndex: 'order_channel_str',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('门店&机器人'),
                dataIndex: 'store_name',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('门店GO'),
                dataIndex: 'delivery_method',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('创建时间'),
                dataIndex: 'created_at',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('完成时间'),
                dataIndex: 'receive_at',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('取消时间'),
                dataIndex: 'cancel_time',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('支付方式'),
                dataIndex: 'pay_type_str',
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('交易流水号'),
                dataIndex: 'pay_transaction_no',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('优惠券抵扣'),
                dataIndex: 'dis_coupon_amount',
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('付款时间'),
                dataIndex: 'pay_at',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('其他优惠'),
                dataIndex: 'dis_other_amount',
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('积分抵扣'),
                dataIndex: 'dis_points_amount',
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('收货人姓名'),
                dataIndex: 'receiver_name',
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('收货人手机号'),
                dataIndex: 'receiver_phone',
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('收货人地址'),
                dataIndex: 'receiver_address',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('发货物流公司'),
                dataIndex: 'express_company',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('发货物流单号'),
                dataIndex: 'express_no',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('是否开票'),
                dataIndex: 'is_invoice',
                hideInSearch: true,
                width: 100,
                render: (text, row, index) => {
                    return (
                        <span key={`is_invoice_${index}`}>
                            {row.is_invoice ? intl.get('是') : intl.get('否')}
                        </span>
                    );
                },
            },
            {
                title: intl.get('退款次数'),
                dataIndex: ['after_sale_count_vo', 'only_refund_count'],
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('退货次数'),
                dataIndex: ['after_sale_count_vo', 'return_goods_count'],
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('换货次数'),
                dataIndex: ['after_sale_count_vo', 'exchange_count'],
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('维修次数'),
                dataIndex: ['after_sale_count_vo', 'repair_count'],
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('补发配件次数'),
                dataIndex: ['after_sale_count_vo', 'reissue_accessory_count'],
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('补券次数'),
                dataIndex: ['after_sale_count_vo', 'compensate_coupon_count'],
                hideInSearch: true,
                width: 100,
            },
            {
                title: intl.get('是否退运费'),
                dataIndex: 'is_re_exp',
                hideInSearch: true,
                width: 100,
                render: (text, row) => {
                    return row.is_re_exp ? intl.get('是') : intl.get('否');
                },
            },
            // {
            //     title: '出库影像',
            //     dataIndex: 'video',
            //     hideInSearch: true,
            //     width: 100,
            //     render: (_, row) => {
            //         return row.order_channel === 12 &&
            //             row.robot_company === 1 ? (
            //             <Button
            //                 key="video"
            //                 type="link"
            //                 onClick={() => onVideoClick(row)}
            //             >
            //                 查看
            //             </Button>
            //         ) : (
            //             '-'
            //         );
            //     },
            // },
            {
                title: intl.get('优惠券名称'),
                dataIndex: 'coupon_name',
                hideInSearch: true,
                width: 200,
            },
            {
                title: intl.get('会员手机号'),
                dataIndex: 'member_phone',
                hideInSearch: true,
                width: 150,
            },
        ],

        [afterSaleEnumList],
    );

    const onSelect = (orderId: string, relieve: boolean = true) => {
        if (selectOrders.length >= maxlength) {
            message.warning(intl.get('最多只能绑定 100 个订单'));
            return;
        }

        const index = selectOrders.indexOf(orderId);
        if (index === -1) {
            setSelectOrders([...selectOrders, orderId]);
        } else if (relieve) {
            selectOrders.splice(index, 1);
            setSelectOrders([...selectOrders]);
        } else {
            message.warning(intl.get('已绑定该订单'));
        }
    };

    const onBindOrder = () => {
        const value = formRef.current?.getFieldsValue();
        if (value?.business_child_order_no) {
            onSelect(value.business_child_order_no, false);
            setDisabled(true);
        }
    };

    const handleOk = () => {
        onConfirm(selectOrders);
        onClose?.(false);
    };

    const onCancel = () => {
        onClose?.(false);
    };

    return (
        <Modal
            title={intl.get('绑定订单')}
            open={open}
            width={1000}
            onOk={handleOk}
            onCancel={onCancel}
        >
            <div>
                {intl.get('已选择订单：')}

                {selectOrders.map((item) => (
                    <Tag
                        key={item}
                        color="orange"
                        closable={true}
                        onClose={() => onSelect(item)}
                        style={{ marginBottom: 10 }}
                    >
                        {item}
                    </Tag>
                ))}
            </div>
            <DynamicTable
                className="modal_table"
                actionRef={tableRef}
                formRef={formRef}
                columns={columns}
                request={async (params) => {
                    const { current, pageSize, ...otherParams } = params;
                    setDisabled(true);

                    if (
                        !Object.keys(otherParams)?.some((i) => !!otherParams[i])
                    ) {
                        message.warning(intl.get('请输入订单号搜索'));
                        return {
                            success: false,
                        };
                    }

                    const { data } = await getOrderList({
                        ...otherParams,
                        page_size: pageSize,
                        page_num: current,
                    });

                    if (!data?.list?.length) {
                        setDisabled(false);
                    }
                    return {
                        // 只取第一条数据，避免优享卡订单，展示 12 条情况
                        data: data?.list?.slice(0, 1) || [],
                        total: data?.total || 0,
                    };
                }}
                manualRequest={true}
                search={{
                    span: 18,
                    optionRender: (searchConfig, formProps, dom) => {
                        return [
                            dom[1],
                            <Button
                                key="bind"
                                type="primary"
                                disabled={disabled}
                                onClick={onBindOrder}
                            >
                                {intl.get('直接绑定')}
                            </Button>,
                        ];
                    },
                }}
                options={false}
                onRow={(record) => {
                    return {
                        onClick: () => {
                            onSelect(record.business_child_order_no);
                        },
                    };
                }}
                size="small"
            ></DynamicTable>
        </Modal>
    );
};

export default SelectOrders;
